import TextWithBreaks from '@/components/raytd/convert-line-break'
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from '@/lib/utils'
import { TestSpecification } from '@app.raytd.com/common/types/test-specification'
import { sentenceCase } from 'change-case'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from "react-hook-form"
import { useTestSpecificationData } from '../hooks/useTestSpecifications'
import { TestSuites } from '../types/test-specification'
import { formatTestSuiteType } from '@/utils/formatTestSuiteType'
import { NO_SUBCATEGORY, normalizeSubcategoryKey, hasSubcategoryTier } from '@/app/features/testsuites/specifications/subcategory-tier'


const getTestSuiteTypeBorderVariant = (type: string) => {
  switch (type) {
    case 'compliance':
      return 'border-l-compliance-500';
    case 'condition':
      return 'border-l-condition-500';
    case 'generic':
      return 'border-l-zinc-500';
    default:
      return '';
  }
}


interface TestSuiteItem {
  id: string;
  title: string;
  type: string;
}
interface TestSuiteGroup {
  type: string;
  suites: TestSuiteItem[];
}

const getGroupedByType = (testSuites: TestSuites[]) => {
  return testSuites.reduce((acc, suite) => {
    const type = suite.type === 'generic' ? 'general' : suite.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push({ id: suite.id?.toString(), title: suite.title, type: suite.type });
    return acc;
  }, {} as Record<string, TestSuiteItem[]>);
};

interface SelectionState {
  suite: string | null;
  category: string | null;
  subcategory: string | null;
  step: 'suite' | 'category' | 'subcategory' | 'test';
}

type FoundSpec = {
  suite: TestSuites;
  specification: TestSpecification;
} | undefined;

interface TestSpecificationSelectorProps {
  name: string;
  label: string;
  testSuites: TestSuites[];
  labelComponent?: React.ReactNode;
  onSelect?: (value: number | null) => void;
  onChange?: (value: number) => void; // Added onChange prop
  disabled?: boolean;
}

export const TestSpecificationSelector: React.FC<TestSpecificationSelectorProps> = React.memo(({ name, label, testSuites, labelComponent, onSelect, onChange, disabled }: TestSpecificationSelectorProps) => {
  const { control, watch, setValue } = useFormContext();
  const organizedData = useTestSpecificationData(testSuites);
  //const [step, setStep] = useState<'suite' | 'category' | 'subcategory' | 'test'>('suite');
  //  const [selectedSuite, setSelectedSuite] = useState<string | null>(null);
  //const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  //const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const [selection, setSelection] = useState<SelectionState>({
    suite: null,
    category: null,
    subcategory: null,
    step: 'suite'
  });


  // const initialValue = watch(name);
  const initialValue = useWatch({
    control,
    name
  });

  const getSelectedTest = useCallback((specId: number) => {
    return testSuites.reduce<FoundSpec>((found, suite) => {
      if (found) return found;
      const specification = suite.specifications.find(spec => spec.id === Number(specId));
      return specification ? { suite, specification } : undefined;
    }, undefined);
  }, [testSuites]);

  const updateSelection = useCallback((selectedTestId: number | null) => {
    if (selectedTestId === null) {
      // setSelectedSuite(null);
      // setSelectedCategory(null);
      // setSelectedSubcategory(null);
      // setStep('suite');

      setSelection({
        suite: null,
        category: null,
        subcategory: null,
        step: 'suite'
      });

    } else {
      // const selectedTest = testSpecifications.find(spec => spec.id === Number(newValue));
      const selectedTest = testSuites.reduce<FoundSpec>((found, suite) => {
        if (found) return found;
        const specification = suite.specifications.find(spec => spec.id === Number(selectedTestId));
        return specification ? { suite, specification } : undefined;
      }, undefined);

      if (selectedTest) {
        setSelection({
          suite: selectedTest.suite.id?.toString() || null,
          category: selectedTest.specification.category || null,
          // NO_SUBCATEGORY ('') is a valid selection, not "nothing selected" —
          // normalize rather than `|| null`, or the test step can't resolve.
          subcategory: normalizeSubcategoryKey(selectedTest.specification.subcategory),
          step: 'test'
        });
        onSelect(Number(selectedTestId));
      }
    }
  }, [testSuites, onSelect]);

  useEffect(() => {
    if (initialValue !== undefined) {
      updateSelection(initialValue);
    } else {
      updateSelection(null);
    }
  }, [initialValue, updateSelection]);

  const filteredTestSpecifications = useMemo(() => {
    if (!search) return [];

    return testSuites
      .flatMap(suite => suite.specifications)
      .filter(spec => spec.label?.toLowerCase().includes(search.toLowerCase()));
  }, [testSuites, search]);

  const groupedSearchResults = useMemo(() => {
    const grouped: { [key: string]: TestSpecification[] } = {};

    filteredTestSpecifications.forEach(spec => {
      const testSuite = testSuites.find(suite => suite.id === spec.test_suite_id);

      let key;
      if (testSuite.type === 'compliance') {
        // Omit the sub-category segment when the spec has no tier (ADR-0009),
        // rather than trailing a "> " with nothing after it.
        key = [testSuite.title, spec.category, hasSubcategoryTier(spec.subcategory) ? spec.subcategory : null]
          .filter(Boolean)
          .join(' > ');
      } else {
        key = `${formatTestSuiteType(testSuite.type)}`;
      }
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(spec);
    });

    return grouped;

  }, [testSuites, filteredTestSpecifications]);

  const goToTestSuites = useCallback(() => {
    setSelection({
      suite: null,
      category: null,
      subcategory: null,
      step: 'suite'
    });
  }, []);

  const goToCategory = useCallback(() => {
    if (selection?.suite) {
      setSelection(prev => ({
        ...prev,
        category: null,
        subcategory: null,
        step: 'category'
      }));
    }
  }, [selection?.suite]);
  // const goToCategory = useCallback(() => {
  //   if (selectedSuite) {
  //     setStep('category');
  //     setSelectedCategory(null);
  //     setSelectedSubcategory(null);
  //   }
  // }, [selectedSuite]);

  const goToSubcategory = useCallback(() => {
    if (selection?.suite && selection?.category) {
      setSelection(prev => ({
        ...prev,
        subcategory: null,
        step: 'subcategory'
      }));
    }
  }, [selection?.suite, selection?.category]);
  // const goToSubcategory = useCallback(() => {
  //   if (selectedSuite && selectedCategory) {
  //     setStep('subcategory');
  //     setSelectedSubcategory(null);
  //   }
  // }, [selectedSuite, selectedCategory]);

  const handleSelect = useCallback((value: number) => {
    setOpen(false);
    onChange && onChange(value); // Updated to call onChange
    onSelect && onSelect(Number(value) || null);
  }, [onChange, onSelect]);

  const autoSelectSingleChild = useCallback(async (currentSuite: string, currentCategory: string | null = null, currentSubcategory: string | null = null, depth: number = 0) => {
    if (depth >= 4) return; // Prevent infinite loops by limiting depth

    if (!currentSuite) return;

    // Check current level and get next options. Compare against null, not
    // truthiness — NO_SUBCATEGORY is the empty string and is a real selection.
    let nextOptions: string[] = [];
    if (currentCategory === null) {
      // At suite level, get categories
      nextOptions = Object.keys(organizedData[currentSuite] || {});
    } else if (currentSubcategory === null) {
      // At category level, get subcategories
      nextOptions = Object.keys(organizedData[currentSuite][currentCategory] || {});
    } else {
      // At subcategory level, get tests
      nextOptions = (organizedData[currentSuite][currentCategory][currentSubcategory] || [])
        .map(test => test.label);
    }

    // If exactly one option exists, auto-select it
    if (nextOptions.length === 1) {
      if (currentCategory === null) {
        setSelection(prev => ({
          ...prev,
          category: nextOptions[0],
          step: 'subcategory'
        }));
        // Recursively check next level
        await autoSelectSingleChild(currentSuite, nextOptions[0], null, depth + 1);
      } else if (currentSubcategory === null) {
        setSelection(prev => ({
          ...prev,
          subcategory: nextOptions[0],
          step: 'test'
        }));
        // Recursively check next level
        await autoSelectSingleChild(currentSuite, currentCategory, nextOptions[0], depth + 1);
      } else {
        // At test level, select the test
        const test = organizedData[currentSuite][currentCategory][currentSubcategory][0];
        // onChange && onChange(test.id);
        setValue(name, test.id, { shouldDirty: true, shouldValidate: true })
        handleSelect(test.id);
      }
    }
  }, [organizedData, onChange, handleSelect]);


  const renderSearchResults = useCallback((onChange: (value: number) => void) => (
    <>
      {Object.entries(groupedSearchResults).map(([path, specs]) => (
        <CommandGroup key={path} heading={path}
        
        >
          {specs.map((spec) => (
            <CommandItem
              key={spec.id}
              value={spec.id.toString()}
              className={cn(
                "border-l-2 border-l-zinc-500 ml-2 rounded-none mb-0.5 my-1",
                getTestSuiteTypeBorderVariant(spec.type)
              )}
      
              onSelect={() => {
                onChange(spec.id);
                handleSelect(spec.id);
              }}
            >
              {spec.label}
            </CommandItem>
          ))}
        </CommandGroup>
      ))}
    </>
  ), [groupedSearchResults, handleSelect]);

  const groupedByType = useMemo(() =>
    getGroupedByType(testSuites),
    [testSuites]
  );

  const renderContent = useCallback((onChange: (value: number) => void) => {
    // console.log('TestSpecificationSelector renderContent called');
    if (search) return renderSearchResults(onChange);

    const { suite, category, subcategory, step } = selection;

    switch (step) {
      case 'suite':
        return (
          <>
            {Object.keys(groupedByType).sort().map((type) => (
              <CommandGroup
                key={type}
                heading={sentenceCase(type)}
                className={cn(
                  "border-l-2 border-l-zinc-500 ml-2 rounded-none mb-0.5 my-1",
                  getTestSuiteTypeBorderVariant(type)
                )}
              >
                {groupedByType[type].sort().map(({id, title, type}) => (
                  <CommandItem
                    key={id}
                    value={id}
                    className=''
                    onSelect={() => {
                      setSelection(prev => ({
                        ...prev,
                        suite: id,
                        step: 'category'
                      }));
                      autoSelectSingleChild(id);
                    }}
                  >
                    {title}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </>
        );
      case 'category':
        return suite && organizedData[suite] ? (
          <CommandGroup heading="Select Category">
            {Object.keys(organizedData[suite]).sort().map((category) => (
              <CommandItem
                key={`${suite}-${category}`}
                value={`${suite}-${category}`}
                onSelect={() => {
                  setSelection(prev => ({
                    ...prev,
                    category: category,
                    step: 'subcategory'
                  }));
                  autoSelectSingleChild(suite, category);

                }}
              >
                {category}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null;
      case 'subcategory': {
        if (!suite || !category || !organizedData[suite][category]) return null;

        const subcategoryKeys = Object.keys(organizedData[suite][category]);
        // Specs with no sub-category tier (ADR-0009) get no heading node — they
        // are offered directly here, next to the real sub-categories.
        const tierlessTests = organizedData[suite][category][NO_SUBCATEGORY] ?? [];

        return (
          <>
            {subcategoryKeys.length > 1 || tierlessTests.length === 0 ? (
              <CommandGroup heading="Select Subcategory">
                {subcategoryKeys.filter(key => key !== NO_SUBCATEGORY).sort().map((subcategory) => (
                  <CommandItem
                    key={`${suite}-${category}-${subcategory}`}
                    value={`${suite}-${category}-${subcategory}`}
                    onSelect={() => {
                      setSelection(prev => ({
                        ...prev,
                        subcategory: subcategory,
                        step: 'test'
                      }));
                      autoSelectSingleChild(suite, category, subcategory);

                    }}
                  >
                    {subcategory}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {tierlessTests.length > 0 && (
              <CommandGroup heading="Select Test">
                {tierlessTests.map((test) => (
                  <CommandItem
                    key={test.id}
                    value={test.id.toString()}
                    onSelect={() => {
                      onChange(test.id);
                      handleSelect(test.id);
                    }}
                    className="flex flex-col justify-center items-start border-b border-b-muted"
                  >
                    <div>{test.label}</div>
                    <div className="text-xs text-zinc-500">{test.description}</div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        );
      }
      case 'test':
        return (suite && category && subcategory !== null &&
          organizedData[suite]?.[category]?.[subcategory]) ? (
          <CommandGroup heading="Select Test">
            {organizedData[suite]?.[category]?.[subcategory].map((test) => (
              <CommandItem
                key={test.id}
                value={test.id.toString()}
                onSelect={() => {
                  onChange(test.id);
                  handleSelect(test.id);
                }}
                className="flex flex-col justify-center items-start border-b border-b-muted"
              >
                <div>{test.label}</div>
                <div className="text-xs text-zinc-500">{test.description}</div>

              </CommandItem>
            ))}
          </CommandGroup>
        ) : null;
    }
  }, [
    search,
    selection,
    organizedData,
    renderSearchResults,
    handleSelect,
    groupedByType
  ]);

  const renderFixedHeader = useCallback(() => {
    if (search) return null;

    const { suite: suiteId, category, subcategory } = selection;

    const suite = testSuites.find(suite => suite.id?.toString() === suiteId)?.title;

    let path = [];
    if (suite) {
      path.push(`${suite}`);
    }
    if (category) {
      path.push(category);
    }
    if (subcategory) {
      path.push(subcategory);
    }

    if (path.length === 0) return null;

    return (
      <div className="sticky top-0 bg-white dark:bg-gray-800 p-2 border-b flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center space-x-1">
          {path.map((item, index) => (
            <span key={item} className="flex items-center">
              {index > 0 && <ChevronRight className="h-4 w-4 mx-1" />}
              <button
                onClick={() => {
                  if (index === 0) goToTestSuites();
                  else if (index === 1) goToCategory();
                  else if (index === 2) goToSubcategory();
                }}
                className="hover:underline focus:outline-none"
              >
                {item}
              </button>
            </span>
          ))}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={goToTestSuites}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }, [search, selection, testSuites, goToTestSuites, goToCategory, goToSubcategory]);

  const renderDisplayValue = useCallback(() => {
    if (initialValue) {
      const selectedSpec = getSelectedTest(initialValue);
      if (!selectedSpec) {
        return "Select Test Specification";
      }
      //return selectedSpec ? selectedSpec.specification.label : "Select Test Specification";
      return <div className={cn("flex flex-col items-start space-y-0.5 border-l-2 pl-2", getTestSuiteTypeBorderVariant(selectedSpec.suite.type))}>
        <div className="text-sm text-muted-foreground">{selectedSpec.suite.title}</div>

        {selectedSpec.suite.type === 'compliance' && (
          <>
            <div className="text-sm text-muted-foreground">{selectedSpec.specification.category}</div>
            {/* No sub-category tier → omit the line entirely (ADR-0009). */}
            {hasSubcategoryTier(selectedSpec.specification.subcategory) && (
              <div className="text-sm text-muted-foreground" data-testid="selected-spec-subcategory">
                {selectedSpec.specification.subcategory}
              </div>
            )}
            <div className="text-base font-medium">{selectedSpec.specification.label}</div>
          </>
        )}
        {selectedSpec.suite.type === 'condition' && (
          <div className="text-base font-medium">{formatTestSuiteType('condition')} Test</div>
        )}

        {selectedSpec.suite.type === 'generic' && (
          <div className="text-base font-medium">{formatTestSuiteType('generic')} Test</div>
        )}
        {
          selectedSpec.suite.type === 'compliance' && (
            <div className="text-zinc-600 font-light text-sm text-wrap text-left">
              <TextWithBreaks>
                {selectedSpec.specification.description}
              </TextWithBreaks>
            </div>
          )
        }
      </div >;

    } else {
      return "Select Test Specification";
    }
  }, [initialValue, getSelectedTest]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {labelComponent || <FormLabel>{label}</FormLabel>}
          <FormControl>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  disabled={disabled}
                  className="h-full w-full justify-between"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(true);
                  }}
                >
                  {/* {initialValue
                    ? (() => {
                      // const selectedSpec = testSuites.find(spec => spec.id === Number(value));
                      const selectedSpec = getSelectedTest(initialValue);
                      return selectedSpec ? selectedSpec.specification.label : "Select Test Specification";
                    })()
                    : "Select Test Specification"} */}
                  {renderDisplayValue()}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="min-w-[400px] w-[var(--radix-popover-trigger-width)] p-0 popover-content"
                side="bottom"
                align="start"
                modal={true}
                onOpenAutoFocus={e => e.preventDefault()}
              >
                <Command className="max-h-[300px] overflow-y-auto" shouldFilter={false}>
                  <CommandInput
                    placeholder="Search test specifications..."
                    value={search}
                    onValueChange={setSearch}
                    className="w-full"
                    aria-label="Search spaces"
                  />
                  {renderFixedHeader()}
                  <CommandList>
                    <CommandEmpty>No test specification found.</CommandEmpty>
                    {renderContent(field.onChange)}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </FormControl>
        </FormItem>
      )}
    />
  );
});

export default TestSpecificationSelector;

